import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { EventService, JobsService, TaskService } from '../../../../services';
import {
  JobScreenDriverNotesJob,
  JobScreenDriverNotesTask,
  JobScreenDriverNotesTaskIndividual,
} from '../../../../interfaces/screen-specific/job-screen';
import { OrganisationTypeEnum } from '../../../../enums/organisation-types';
import { JobScreenCommonService } from '../job-screen-common.service';
import { ITaskNote } from '../../../../interfaces/task-info';
import { Message } from '../../../../classes/Message';
import { FormBuilder, FormGroup, UntypedFormControl } from '@angular/forms';
import { MessageService } from 'primeng';
import { RefreshJobEventTypeEnum } from '../../../../enums/refresh-job-event-type.enum';
import { Router } from '@angular/router';
import { ConfirmationService } from 'primeng/api';

@Component({
  selector: 'job-screen-driver-notes',
  templateUrl: './job-screen-driver-notes.component.html',
  styleUrls: ['./job-screen-driver-notes.component.scss', '../../common/job-screen-common.scss'],
})
export class JobScreenDriverNotesComponent implements OnInit, OnChanges {
  @Input('jobId') jobId!: number;
  @Input('orderId') orderId!: number;
  @Input('taskId') taskId!: number;
  @Input('organisationId') organisationId!: number;
  @Input('organisationTypeId') organisationTypeId!: number;

  public selectedTask: JobScreenDriverNotesTask;
  public job: JobScreenDriverNotesJob;

  loadingTask: boolean;
  pickupContactEdit: boolean;
  dropoffContactEdit: boolean;
  isShowAddDriverNotes: boolean;
  newNoteDescription: string;
  newNoteIsPickup: boolean;
  isBusyAddDriverNotes: boolean;
  noteTypes: any[];
  loadingJob: boolean;

  public driverNoteToEdit: { isPickUp: boolean; description: string; id?: number };
  public showEditDriverNoteDialog: boolean = false;

  protected readonly OrganisationTypeEnum = OrganisationTypeEnum;

  constructor(
    private taskService: TaskService,
    private fb: FormBuilder,
    private jobService: JobsService,
    private messageService: MessageService,
    public commonService: JobScreenCommonService,
    private eventService: EventService,
    private confirmationService: ConfirmationService,
  ) {}

  ngOnInit(): void {
    this.fetchJob(this.jobId);
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes.taskId) {
      this.fetchTask(this.taskId).then(() => {});
    }
  }

  fetchJob(jobId: number) {
    this.loadingJob = true;
    this.jobService
      .getForComponent(jobId, 'job-screen-driver-notes')
      .then((job: JobScreenDriverNotesJob) => {
        this.job = job;
        this.loadingJob = false;
      });
  }

  async fetchTask(taskId: number) {
    this.loadingTask = true;
    this.taskService.getForComponent(taskId, 'job-screen-driver-notes').then((task) => {
      this.selectedTask = task as JobScreenDriverNotesTask;
      this.loadingTask = false;
    });
  }

  onSaveContactDetails(driverNotes: JobScreenDriverNotesTaskIndividual, isPickup: boolean) {
    this.dropoffContactEdit = false;
    this.pickupContactEdit = false;
    if (isPickup) {
      this.taskService
        .SaveContactDetails({
          taskId: this.selectedTask.id,
          pickupForename: driverNotes.forename,
          pickupSurname: driverNotes.surname,
          pickupEmail: driverNotes.email,
          pickupSecondaryEmail: driverNotes.secondaryEmail,
          pickupPhone1: driverNotes.phone1,
          pickupPhone2: driverNotes.phone2,
          pickupNotification: driverNotes.notification,
          // Do not update dropoff details
          dropoffForename: this.selectedTask.dropoffForename,
          dropoffSurname: this.selectedTask.dropoffSurname,
          dropoffEmail: this.selectedTask.dropoffEmail,
          dropoffSecondaryEmail: this.selectedTask.dropoffSecondaryEmail,
          dropoffPhone1: this.selectedTask.dropoffPhone1,
          dropoffPhone2: this.selectedTask.dropoffPhone2,
          dropoffNotification: this.selectedTask.dropoffNotification,
        })
        .then(() => {
          this.selectedTask.pickupForename = driverNotes.forename;
          this.selectedTask.pickupSurname = driverNotes.surname;
          this.selectedTask.pickupEmail = driverNotes.email;
          this.selectedTask.pickupSecondaryEmail = driverNotes.secondaryEmail;
          this.selectedTask.pickupPhone1 = driverNotes.phone1;
          this.selectedTask.pickupPhone2 = driverNotes.phone2;
          this.selectedTask.pickupNotification = driverNotes.notification;
          this.eventService.RefreshJobEvent.emit(RefreshJobEventTypeEnum.RefreshHistory);
        });
    } else {
      this.taskService
        .SaveContactDetails({
          taskId: this.selectedTask.id,
          // Do not update pickup details
          pickupForename: this.selectedTask.pickupForename,
          pickupSurname: this.selectedTask.pickupSurname,
          pickupEmail: this.selectedTask.pickupEmail,
          pickupSecondaryEmail: this.selectedTask.pickupSecondaryEmail,
          pickupPhone1: this.selectedTask.pickupPhone1,
          pickupPhone2: this.selectedTask.pickupPhone2,
          pickupNotification: this.selectedTask.pickupNotification,
          dropoffForename: driverNotes.forename,
          dropoffSurname: driverNotes.surname,
          dropoffEmail: driverNotes.email,
          dropoffSecondaryEmail: driverNotes.secondaryEmail,
          dropoffPhone1: driverNotes.phone1,
          dropoffPhone2: driverNotes.phone2,
          dropoffNotification: driverNotes.notification,
        })
        .then(() => {
          this.selectedTask.dropoffForename = driverNotes.forename;
          this.selectedTask.dropoffSurname = driverNotes.surname;
          this.selectedTask.dropoffEmail = driverNotes.email;
          this.selectedTask.dropoffSecondaryEmail = driverNotes.secondaryEmail;
          this.selectedTask.dropoffPhone1 = driverNotes.phone1;
          this.selectedTask.dropoffPhone2 = driverNotes.phone2;
          this.selectedTask.dropoffNotification = driverNotes.notification;
          this.eventService.RefreshJobEvent.emit(RefreshJobEventTypeEnum.RefreshHistory);
        });
    }
  }

  toDisplayDriverNotes(): boolean {
    if (this.organisationTypeId !== OrganisationTypeEnum.Supplier) {
      return true;
    }

    if (!this.job?.supplierId) {
      return false;
    }

    if (this.job.supplierId === this.organisationId) {
      if (!this.commonService.isJobCompleted(this.job)) {
        return true;
      }
    }

    return false;
  }

  setChangePickupDialog(value: boolean) {
    this.pickupContactEdit = value;
  }

  getNotes(notes: ITaskNote[], isPickup: boolean): ITaskNote[] {
    if (!notes) return [];

    let result = [];
    notes.forEach((x) => {
      x.description = x.description.replace(/\|\-|-\|/g, '');
      if (x.isPickUp == isPickup) {
        result.push(x);
      }
    });

    result.sort((x) => new Date(x.created).getTime());
    return result;
  }

  setChangeDropoffDialog(value: boolean) {
    this.dropoffContactEdit = value;
  }

  showAddDriverNotes(isPickup: boolean) {
    this.isShowAddDriverNotes = true;
    this.noteTypes = [];
    this.noteTypes.push({ label: 'Pick-up', value: true });
    this.noteTypes.push({ label: 'Drop-off', value: false });
    this.newNoteDescription = '';
    this.newNoteIsPickup = isPickup;
  }

  showEditDriverNote(note: ITaskNote) {
    this.driverNoteToEdit = { ...note };
    this.showEditDriverNoteDialog = true;
  }

  closeAddDriverNotes() {
    this.isShowAddDriverNotes = false;
  }

  saveAddDriverNotes() {
    if (!this.newNoteDescription || this.newNoteDescription.length == 0) {
      return;
    }

    this.isBusyAddDriverNotes = true;

    this.jobService
      .addDriverNotes({
        taskId: this.selectedTask.id,
        note: this.newNoteDescription,
        isPickup: this.newNoteIsPickup,
      })
      .then(() => {
        this.isBusyAddDriverNotes = false;
        void this.fetchTask(this.taskId);
        this.eventService.RefreshJobEvent.emit(RefreshJobEventTypeEnum.RefreshHistory);
        this.messageService.add(Message.Success('Changes were successful.', 'Change Driver Notes'));
        this.isShowAddDriverNotes = false;
      })
      .catch(() => {
        this.isBusyAddDriverNotes = false;
        this.messageService.add(
          Message.Error(
            'An error occurred while trying to modify the data. Try again. If the error persists, contact your administrator.',
            'Change Driver Notes',
          ),
        );
      });
  }
  async saveDriverNote() {
    await this.taskService.updateDriverNote(
      this.taskId,
      this.driverNoteToEdit.id,
      this.driverNoteToEdit.description,
    );
    this.showEditDriverNoteDialog = false;
    void this.fetchTask(this.taskId);
    this.eventService.RefreshJobEvent.emit(RefreshJobEventTypeEnum.RefreshHistory);
  }

  removeDriverNote(noteId: number) {
    this.confirmationService.confirm({
      message: `Are you sure you want to remove this note? This action cannot be undone`,
      acceptLabel: 'Remove',
      rejectLabel: 'Cancel',
      accept: async () => {
        await this.taskService.deleteDriverNote(this.taskId, noteId);
        void this.fetchTask(this.taskId);
        this.eventService.RefreshJobEvent.emit(RefreshJobEventTypeEnum.RefreshHistory);
      },
    });
  }
}
